'use client';

import { useEffect, useMemo, useRef, useState } from 'react';
import { Check, CheckCircle2, Circle, MapPin, Rocket, RotateCw, Trash2, TriangleAlert, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';
import { usePointerDrag } from '@/hooks/usePointerDrag';
import type { Drone, Pilot, Site } from '@/types/fleet';
import type { KeepInFence } from '@/lib/mission-planning';
import {
  CANVAS_ALT_DEFAULT_M,
  CANVAS_ALT_MAX_M,
  CANVAS_ALT_MIN_M,
  MAX_ROUTE_WAYPOINTS,
  MISSION_NAME_MAX,
  canvasGates,
  deriveMissionName,
  effectiveWaypointAltitude,
  draftRouteDistanceM,
  nameStampAt,
  nowLaunchGate,
  parseAltitudeInput,
  type WhenMode,
} from '../missions/_components/missionCanvasShared';
import { orbitLaunchWarning } from '@/lib/copy/mission';
import {
  MissionReadinessRail,
  type SelectedVerdictReport,
} from '../missions/_components/MissionReadinessRail';
import { RailSelect, UNASSIGNED, crewValueToId } from '../missions/_components/MissionCrewSelect';
import {
  MISSION_TYPES,
  MISSION_TYPE_DESCRIPTIONS,
  MISSION_TYPE_LABELS,
  isFlyableMissionType,
  missionTypeUnavailableReason,
  waypointCountRangeForType,
  DEFAULT_ORBIT_RADIUS_M,
  MIN_ORBIT_RADIUS_M,
  MAX_ORBIT_RADIUS_M,
  type MissionType,
} from '@/lib/mission-type';
import { useMissionCommit } from '../missions/_components/useMissionCommit';
import { clampDockSheetWidth } from './CopDockSheet';
import { RouteWaypointList } from './RouteWaypointList';
import type { DrawnRouteController } from './useDrawnRoute';
import { GotoReliefQuestionBar } from './GotoReliefQuestionBar';
import { aMissionType } from '@/lib/copy';
import { useOrgTimezone } from '@/hooks/useOrgTimezone';
import { formatDateTimeFull } from '@/lib/dateFormat';
import { utcInputValueToIso } from '@/app/(dashboard)/missions/_components/missionDetailShared';

// ---------------------------------------------------------------------------
// Composing a mission WITHOUT leaving the air picture.
//
// The route is drawn on the live COP map — the same map still painting tracks,
// alerts and every other mission route underneath. That is the whole point: the
// full-screen canvas this replaces reproduced, inside a single page, exactly the
// cost of the separate missions page it was meant to remove. An operator
// planning a sortie could not see the air they were planning into.
//
// Nothing here is a second implementation. The gates (`canvasGates`,
// `nowLaunchGate`), the fleet picker (`MissionReadinessRail`), the derived name
// and the commit path (`useMissionCommit`) are the canvas's, unchanged. What is
// new is the shape: a right-column panel instead of a full-screen mode, and a
// route validated against keep-in fences as it is drawn, which the canvas never
// did because it could not see them.
// ---------------------------------------------------------------------------

/** Copy for the NOW lane's rail. A NO-GO aircraft is why LAUNCH will not arm, not a footnote. */
const NOW_LANE_RAIL_COPY = {
  unassignedLabel: 'Unassigned. A launch needs an aircraft — pick one below.',
  noGoNote: (callsign: string) =>
    `${callsign} is NO-GO right now, so LAUNCH stays disabled. Pick another aircraft, or switch to Plan to save this as a draft.`,
};

/**
 * Copy for the rail once HOLD THIS STATION is ticked.
 *
 * The checkbox sits directly above this picker, and ticking it changes what the pick MEANS - from
 * the aircraft that flies this mission to the first aircraft of a rotation that keeps the station
 * manned. Nothing on the rail used to move, so an operator got the strongest possible hint that the
 * pick below was the coverage pick, and then met an apparently empty rotation picker one panel
 * later. The legend and the note are what make the second step read as ADDING RELIEF rather than as
 * the same question asked twice.
 *
 * `unassignedLabel` and `noGoNote` are deliberately absent: whether an aircraft is required, and
 * why LAUNCH will not arm, are decided by the LANE, not by this checkbox. `holdRailCopy` below
 * layers this over the lane's own copy rather than replacing it.
 */
const HOLD_STATION_RAIL_COPY = {
  legend: 'First on station',
  // AN AGENT, and a step. "Relief aircraft join the rotation" reads as a system promise - that
  // they will be added - when it is work the operator has to do, and if they never do it nothing
  // joins.
  note: 'You add relief aircraft in the next step, after the mission is created.',
  // THE PLAN LANE STILL OFFERS UNASSIGNED, and creation is not gated on an aircraft (`canvasGates`:
  // only the name gates it). So an operator can be told a first aircraft is established, leave this
  // empty, and meet the empty rotation picker this whole change exists to remove. The lane decides
  // whether an aircraft is REQUIRED and that is unchanged; what is added is what leaving it empty
  // costs, which is the checkbox's business and not the lane's.
  unassignedLabel: 'Unassigned. Create it without one, and pick the first aircraft in the next step.',
};

/**
 * The one tooltip the six "?" icons share. A literal rather than a `useId`, because only one
 * is ever mounted — whichever "?" the pointer is over owns it — so `aria-describedby` is set
 * on exactly one trigger at a time. Compose is a singleton on the COP, so nothing collides.
 */
const MISSION_TYPE_TIP_ID = 'compose-mission-type-tip';

/**
 * The type picker's column count, and the Tailwind class that realises it.
 *
 * Stated as a pair, adjacent, because they MUST agree and Tailwind cannot be handed an
 * interpolated class name. The tooltip anchors to its cell's outer edge and works that edge
 * out from `index % MISSION_TYPE_COLUMNS`; with the two 90 lines apart, adding a responsive
 * column count would silently anchor tooltips to the wrong side and run them off the scroller.
 */
const MISSION_TYPE_COLUMNS = 2;
const MISSION_TYPE_GRID_CLASS = 'grid grid-cols-2 gap-1';

/**
 * Everything the attach wizard needs about a mission compose has just committed.
 *
 * An object rather than positional arguments: it reached five fields, two of them nullable and
 * adjacent, which is where a call site starts silently passing the right value in the wrong slot.
 */
export interface CoverageHandoff {
  missionId: string;
  missionType: MissionType;
  /** The LAST waypoint - the station coverage is measured from. Null when the route did not persist. */
  hold: { latitude: number; longitude: number; altitude: number } | null;
  /**
   * The aircraft this mission was assigned, so the wizard can seed its rotation with it instead of
   * opening on an empty picker. Null on a plan-lane mission created without one.
   */
  assignedDroneId: string | null;
  /**
   * The commit was a LAUNCH, not a save.
   *
   * The sheet reads it for one thing only: what it says happened. "The mission is created" is
   * false on this lane - the launch POST has been accepted and an aircraft is being commanded off
   * the pad - and that sentence used to be safe only because the NOW lane never reached the wizard
   * at all.
   */
  launched: boolean;
}

export interface CopMissionComposeProps {
  route: DrawnRouteController;
  drones: Drone[];
  pilots: Pilot[];
  sites: Site[];
  /** Reference lists have settled. */
  ready: boolean;
  /** They failed to load — different from an org with no drones. */
  loadError: boolean;
  /** Active keep-in fences, for the route hint. The route hook does the checking. */
  keepInFences: KeepInFence[];
  /**
   * The mission altitude, reported up because the route lives on the page (the
   * map draws it and validates it) while the altitude field lives here. It is
   * the only draft value the route needs: the site is metadata, not a launch
   * point, so it never reaches the map.
   */
  onAltitudeChange: (altitudeM: number) => void;
  /**
   * 022. The chosen mission type, reported up for the same reason the altitude is: the page
   * owns the consequences. It re-arms the map for the new geometry and re-caps the route,
   * neither of which this panel can reach.
   */
  missionType: MissionType;
  onMissionTypeChange: (type: MissionType) => void;
  /**
   * The orbit radius, reported up so the map can draw the ring the aircraft will fly. Null
   * whenever the box does not hold a usable number, so the map draws nothing rather than a
   * circle at some last-good radius the operator has since typed over.
   */
  onOrbitRadiusChange: (radiusM: number | null) => void;
  /**
   * True while the map is armed for this compose — the operator can place points.
   *
   * Compose is OPEN whether or not it is armed: point entry ends on a double-click
   * or Escape, and the drawn route and this panel stay, because the mission still
   * has to be committed. Which means a disarmed compose needs a way BACK — without
   * one, the only route to placement was a there-and-back type switch, which drops
   * points the intermediate type cannot hold (issue #641).
   */
  armed: boolean;
  onArm: () => void;
  /** Close compose and disarm the map. */
  onClose: () => void;
  /** A mission now exists — refresh the queue and stand compose down. */
  onCreated: (missionId: string) => void;
  onCreatedWithoutId: () => void;
  /**
   * The mission types the server will accept continuous coverage on, or null while unknown.
   *
   * Three-state, and the third state matters: null hides the offer because nobody has answered,
   * an empty list hides it because the server says nothing qualifies. Conflating them would let a
   * slow or failed read quietly remove a capability with nothing on screen to explain it.
   */
  coverageCapableTypes?: MissionType[] | null;
  /**
   * The operator ticked "hold this station" and the mission was created.
   *
   * Called BEFORE `onCreated`, with the last waypoint the route ended on, so the page can open the
   * attach wizard over the map pre-filled. Fired only from the create path - the rescue re-entries
   * below call `onCreated` directly and must not re-offer coverage for a mission that already
   * exists and may already have it.
   */
  onCoverageRequested?: (request: CoverageHandoff) => void;
  onOpenMission: (missionId: string) => void;
  /** Shared with every other right-column sheet — one column, one width. */
  width: number;
  onWidthChange: (width: number) => void;
}

/**
 * The compose panel: WHERE is on the map, WHO and WHEN are here, and the strip
 * at the bottom is the commit.
 */
export function CopMissionCompose({
  route,
  drones,
  pilots,
  sites,
  ready,
  loadError,
  keepInFences,
  onAltitudeChange,
  missionType,
  onMissionTypeChange,
  onOrbitRadiusChange,
  armed,
  onArm,
  onClose,
  onCreated,
  onCreatedWithoutId,
  coverageCapableTypes,
  onCoverageRequested,
  onOpenMission,
  width,
  onWidthChange,
}: CopMissionComposeProps) {
  const [droneId, setDroneId] = useState<string | null>(null);
  const [pilotId, setPilotId] = useState(UNASSIGNED);
  const [siteId, setSiteId] = useState(UNASSIGNED);
  const [altitudeRaw, setAltitudeRaw] = useState(String(CANVAS_ALT_DEFAULT_M));
  const [scheduledAt, setScheduledAt] = useState('');
  const orgTimezone = useOrgTimezone();
  /**
   * The org-zone reading of what is typed in the UTC departure box, or null while
   * it is empty or mid-edit. An operator at UTC+3 must not schedule three hours
   * off because the box said 02:30 and the clock above it says 05:30.
   */
  const localDeparture = (() => {
    const iso = scheduledAt === '' ? null : utcInputValueToIso(scheduledAt);
    return iso === null ? null : formatDateTimeFull(iso, orgTimezone);
  })();
  const [whenMode, setWhenMode] = useState<WhenMode>('later');
  const [holdContinuously, setHoldContinuously] = useState(false);
  /**
   * THE INTENT DIES WITH THE OFFER.
   *
   * Switching to a type the server cannot hold takes the checkbox off the panel, and the only
   * reasonable reading of that is that the option went with it. Leaving the flag set meant CREATE
   * then opened a modal wizard over the map for a mission whose `capabilityBlocker` refuses it
   * outright - an un-asked-for dialog whose sole outcome is a refusal.
   *
   * Adjusted during render rather than in an effect, for the reason `useCopRotation` states: an
   * effect clears it one commit late, and that commit is a painted frame.
   */
  const offerable = coverageCapableTypes?.includes(missionType) === true;
  if (holdContinuously && !offerable) setHoldContinuously(false);
  const [nameOverride, setNameOverride] = useState<string | null>(null);
  const [selectedVerdict, setSelectedVerdict] = useState<SelectedVerdictReport>({ row: null, stale: true });
  const [confirmDiscard, setConfirmDiscard] = useState(false);

  // 022. The type itself is the page's state (it re-caps the route and re-arms the map); the
  // orbit's own parameters are nobody else's business and stay here.
  const [orbitRadiusRaw, setOrbitRadiusRaw] = useState(String(DEFAULT_ORBIT_RADIUS_M));
  const [orbitDirection, setOrbitDirection] = useState<'cw' | 'ccw'>('cw');
  /** A type switch that would drop placed points asks first. */
  const [pendingTypeSwitch, setPendingTypeSwitch] = useState<MissionType | null>(null);
  /**
   * The "?" help tooltip: what the pointer is on, what focus is on, and whether Escape has
   * shut it. Three pieces of state rather than one, because hover and focus are INDEPENDENT
   * triggers and a single `tipType` let each clobber the other:
   *
   *  - focus a "?" by keyboard, then sweep the mouse across a different "?" and away — the
   *    focused tooltip vanished and would not come back until that icon was blurred and
   *    re-focused;
   *  - park the pointer on a "?", then Tab onto and off that same icon — `onBlur` closed the
   *    tooltip while the pointer was still sitting on it, and no `mouseover` fires for
   *    movement WITHIN an element, so it stayed shut.
   *
   * WCAG 1.4.13 wants the content persistent while the trigger is hovered OR focused, which is
   * exactly `hovered ?? focused`. Hover wins when both are set — it is the more recent intent.
   *
   * A real tooltip rather than a native `title`: `title` is invisible to the keyboard and
   * cannot be styled. It is ALSO the only way to read what an unavailable type is — that
   * button is dead, so the explanation cannot hang off the control itself.
   */
  const [hoveredTipType, setHoveredTipType] = useState<MissionType | null>(null);
  const [focusedTipType, setFocusedTipType] = useState<MissionType | null>(null);
  const [tipDismissed, setTipDismissed] = useState(false);
  const tipType = tipDismissed ? null : hoveredTipType ?? focusedTipType;

  /** Opening a tooltip clears a previous Escape — a NEW trigger is a new request to see it. */
  const openTip = (type: MissionType, by: 'hover' | 'focus') => {
    setTipDismissed(false);
    if (by === 'hover') setHoveredTipType(type);
    else setFocusedTipType(type);
  };
  /** Only the trigger that is leaving clears itself; the other keeps whatever it was on. */
  const closeTip = (type: MissionType, by: 'hover' | 'focus') => {
    const set = by === 'hover' ? setHoveredTipType : setFocusedTipType;
    set((open) => (open === type ? null : open));
  };

  /**
   * Escape closes the tooltip.
   *
   * On `window` in the CAPTURE phase, and NOT as an `onKeyDown` on the trigger, because the
   * COP page owns a window-capture Escape of its own that disarms the armed map mode and
   * calls `stopPropagation()` (`page.tsx`). Compose opens with the map armed, so that handler
   * runs — and a React `onKeyDown`, dispatched from the root container far below
   * `window`, never saw the key at all. Verified in a browser: the handler simply never fired.
   *
   * `stopPropagation` does not silence other listeners on the same node, so this one runs
   * beside it. That is not enough on its own, though: BOTH would act, and one Escape would
   * close the tooltip AND silently un-arm waypoint placement, which at the time compose had
   * no control to undo (it does now — #641). The page's handler stands down while a tooltip is
   * open — the same guard it already applies to an open dialog — so the first Escape is the
   * tooltip's and the second reaches the map.
   *
   * WCAG 1.4.13 — content shown on hover or focus must be dismissable without moving the
   * pointer, and this tooltip overlays the form beneath it.
   */
  useEffect(() => {
    if (tipType === null) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setTipDismissed(true); };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [tipType]);

  /** Frozen at open, so the derived name's stamp marks when planning started. */
  const openedAt = useRef(new Date());

  const startWidth = useRef(width);
  const { onPointerDown: onResizePointerDown } = usePointerDrag({
    cursor: 'ew-resize',
    onDragStart: () => {
      startWidth.current = width;
    },
    onDrag: ({ dx }) => onWidthChange(clampDockSheetWidth(startWidth.current - dx, window.innerWidth)),
  });

  const { waypoints, selectedIndex, violations, invalidIndices, atCap } = route;

  const selectedSiteId = crewValueToId(siteId);
  const selectedSiteName = sites.find((s) => s.id === selectedSiteId)?.name ?? null;

  // Derived during render so the proposed name always matches what is chosen. The
  // stamp follows the departure once one is set — a mission named for the minute
  // compose happened to open contradicts the departure printed beside it.
  const name = nameOverride
    ?? deriveMissionName(selectedSiteName, waypoints, nameStampAt(scheduledAt, openedAt.current));

  const altitude = useMemo(() => parseAltitudeInput(altitudeRaw), [altitudeRaw]);
  const distanceM = useMemo(() => draftRouteDistanceM(waypoints), [waypoints]);

  const gates = useMemo(
    () => canvasGates(name, droneId, crewValueToId(pilotId), waypoints, missionType),
    [name, droneId, pilotId, waypoints, missionType],
  );

  /** The airframe the launch would use, for the refusals that turn on it rather than on the mission. */
  const selectedDrone = useMemo(
    () => drones.find((d) => d.id === droneId) ?? null,
    [drones, droneId],
  );

  /** 022. Parsed here so an unreadable box blocks the save rather than sending NaN. */
  const orbitRadiusM = useMemo(() => {
    const value = Number.parseFloat(orbitRadiusRaw);
    if (!Number.isFinite(value)) return null;
    if (value < MIN_ORBIT_RADIUS_M || value > MAX_ORBIT_RADIUS_M) return null;
    return value;
  }, [orbitRadiusRaw]);

  /**
   * The orbit's own blocker, kept separate from `gates` because it is not about the route.
   *
   * Mirrors 022's coherence constraint only in the sense of catching it early — the API and the
   * CHECK are still the authority, and a refusal from either is shown verbatim.
   *
   * DECLARED BEFORE `nowGate`, which now takes it: it used to render only as an amber line
   * beside the radius box, near the top of a scrolling panel, while the commit strip at the
   * bottom printed a green "Aircraft and route ready" over an enabled LAUNCH. The strip is what
   * an operator reads before arming, so a fault that stops the launch belongs in it.
   */
  const orbitBlocker = missionType !== 'orbit'
    ? null
    : orbitRadiusM === null
      ? `Set an orbit radius between ${MIN_ORBIT_RADIUS_M} m and ${MAX_ORBIT_RADIUS_M} m`
      : null;

  const nowGate = useMemo(
    () => nowLaunchGate(
      droneId, waypoints, gates.routeWillPersist, gates.createBlocker,
      selectedVerdict.row, selectedVerdict.stale, missionType,
      selectedDrone, orbitBlocker,
    ),
    [
      droneId, waypoints, gates.routeWillPersist, gates.createBlocker, selectedVerdict,
      missionType, selectedDrone, orbitBlocker,
    ],
  );

  /**
   * One stored point — a destination or an orbit's centre.
   *
   * Read off the type's own `max`, so #373 moving HOVER to a route needed nothing here: the
   * label flips from "Point" to "Route", the placement hint from "click the point" to "click
   * waypoints", and the summary from "POINT SET" to "N WP · X km", all from this one line.
   */
  const isSinglePoint = waypointCountRangeForType(missionType).max === 1;

  /**
   * Switching type, asking first when points would be dropped.
   *
   * A type change is not destructive by itself — going from GO TO POINT to FOLLOW ROUTE keeps
   * the one point and lets more be added. It is destructive in the other direction, and that
   * is the case that has to be confirmed: the drawn route exists only in this component's
   * state, so points dropped here cannot be recovered from anywhere.
   */
  const requestTypeSwitch = (next: MissionType) => {
    if (next === missionType) return;
    // Belt and braces with the button's own `disabled`: the picker is the only way in, and
    // both read the same `isFlyableMissionType`, so neither can offer what the other refuses.
    if (!isFlyableMissionType(next)) return;
    const wouldDrop = waypoints.length - waypointCountRangeForType(next).max;
    if (wouldDrop > 0) {
      setPendingTypeSwitch(next);
      return;
    }
    // Clear any confirm still on screen. Without this, an operator who asks for GO TO POINT
    // (prompt appears), thinks better of it and picks OUT & BACK instead is left looking at
    // the GO TO POINT question — and pressing "Switch and drop" then applies the type they
    // moved on from, discarding waypoints they were no longer being asked about.
    setPendingTypeSwitch(null);
    onMissionTypeChange(next);
  };

  const commit = useMissionCommit({
    draft: {
      name,
      siteId: selectedSiteId,
      droneId,
      pilotId: crewValueToId(pilotId),
      altitudeM: altitude.meters,
      waypoints,
      missionType,
      orbit: missionType === 'orbit' && orbitRadiusM !== null
        ? { radiusM: orbitRadiusM, direction: orbitDirection }
        : null,
    },
    gates,
    nowGate,
    whenMode,
    scheduledAt,
    /**
     * THE HAND-OFF, and it is wrapped HERE rather than passed to the page.
     *
     * `useMissionCommit` calls this only on a successful create - both of its call sites are the
     * "route saved" and the "launched" paths. The rescue re-entries in this file call the prop
     * `onCreated` directly instead, and must not re-offer coverage: those are returns to a mission
     * that already exists and may already have a rotation on it.
     *
     * The hold point is the LAST WAYPOINT, which is where the wizard measures the station from
     * (`hold`). Read here, before compose stands down and its copy of the route goes with it. The
     * chosen aircraft rides along for the same reason and to the same end: it seeds the rotation,
     * so the operator is never asked for it twice.
     */
    onCreated: (missionId: string) => {
      /**
       * BOTH LANES NOW, and the reason the NOW lane was once excluded is worth keeping.
       *
       * `useMissionCommit` calls this the instant the launch POST is accepted, and the wizard used
       * to arrive as a 720 px MODAL. On the NOW lane that covered the live map at exactly the
       * moment the aircraft was commanded, hiding golden-flow steps 7 to 11: pre_takeoff, the bay
       * opening, the aircraft reporting ready, the takeoff. So the hand-off was withheld there and
       * replaced with a toast telling the operator to go to the mission page.
       *
       * That reason was an objection to THE MODAL, not to configuring coverage at that moment. The
       * wizard is now a sheet in the right column beside the map (`CopCoverageAttachSheet`), so
       * nothing is covered and the objection does not survive the move. Withholding it anyway
       * would leave one checkbox meaning two different things depending on which button was
       * pressed - and on the lane where the operator is most committed, meaning nothing at all.
       */
      if (holdContinuously) {
        const last = waypoints[waypoints.length - 1];
        onCoverageRequested?.({
          missionId,
          missionType,
          hold: last
            ? {
              latitude: last.lat,
              longitude: last.lng,
              // THE POINT'S OWN ALTITUDE WHEN IT HAS ONE, not the mission field. A waypoint may
              // carry an override (`altitudeM`), and the station is measured where the aircraft
              // will actually hold - handing the wizard the mission default would pre-fill a
              // station height the operator had deliberately changed.
              altitude: effectiveWaypointAltitude(last, altitude.meters),
            }
            : null,
          assignedDroneId: droneId,
          launched: whenMode === 'now',
        });
      }
      onCreated(missionId);
    },
    onCreatedWithoutId,
    onOpenMission,
  });
  const { saving, launchError } = commit;
  const routeRescuePending = commit.routeRescueMissionId !== null;
  const launchRescuePending = commit.launchRescueMissionId !== null;

  /** Everything the operator would lose. Assignments and departure count — real work. */
  const hasWork =
    waypoints.length > 0 || nameOverride !== null || scheduledAt !== '' ||
    droneId !== null || pilotId !== UNASSIGNED || siteId !== UNASSIGNED || whenMode !== 'later';

  /**
   * The mission altitude, reported up because the route lives on the page.
   *
   * The SITE is deliberately not reported with it. A site is metadata on the
   * mission record — which of the operator's locations this sortie belongs to —
   * not a takeoff pad. It used to be sent up as the route's launch point, which
   * drew an 'H' at the site and a leading leg from it into WP1, so choosing or
   * switching a site visibly re-anchored a route the operator had already drawn.
   * The route is what the operator clicked onto the map, and nothing else.
   */
  const altitudeM = altitude.meters;
  useEffect(() => {
    onAltitudeChange(altitudeM);
  }, [altitudeM, onAltitudeChange]);

  /**
   * 022. The orbit radius travels the same way and for the same reason: the ring is drawn on
   * the map, which this panel cannot reach. Null unless the type is `orbit` AND the box holds
   * a usable number, so switching away from an orbit takes the ring off the map with it.
   */
  const reportedOrbitRadiusM = missionType === 'orbit' ? orbitRadiusM : null;
  useEffect(() => {
    onOrbitRadiusChange(reportedOrbitRadiusM);
  }, [reportedOrbitRadiusM, onOrbitRadiusChange]);

  /**
   * Backing out. Compose holds the only copy of the drawn route, so leaving with
   * one drawn always asks — the same rule the canvas applies, for the same reason.
   */
  const requestClose = () => {
    if (saving) return;
    if (launchRescuePending) {
      onCreated(commit.launchRescueMissionId!);
      return;
    }
    if (routeRescuePending || hasWork) {
      setConfirmDiscard(true);
      return;
    }
    onClose();
  };

  /**
   * The rail's copy: the LANE's, plus the hold lane's on top when the box is ticked.
   *
   * Layered rather than switched, because they answer different questions. The lane decides
   * whether an aircraft is required and what a NO-GO costs; the checkbox decides what the pick is
   * FOR. A `holdContinuously ? A : B` would have silently dropped the NOW lane's NO-GO sentence
   * for exactly the operator most likely to need it - one launching a held station right now.
   */
  const holdRailCopy = useMemo(() => {
    const lane = whenMode === 'now' ? NOW_LANE_RAIL_COPY : undefined;
    if (!holdContinuously) return lane;
    // THE LANE WINS ON `unassignedLabel`, and only on that. The hold copy's version describes a
    // choice; the NOW lane's is a REFUSAL ("a launch needs an aircraft"), and a refusal must not be
    // overwritten by a description of something the operator cannot do on that lane anyway.
    return { ...HOLD_STATION_RAIL_COPY, ...lane };
  }, [whenMode, holdContinuously]);

  const firstErrorViolation = violations.find((v) => v.severity === 'error') ?? null;
  const pilotOptions = pilots.map((p) => ({ id: p.id, label: p.full_name }));
  const siteOptions = sites.map((s) => ({ id: s.id, label: s.name }));
  const unavailable = 'Fleet data could not be loaded — retry from the board';

  return (
    <div
      /* `overflow-clip`, never `overflow-hidden`. `hidden` makes this a SCROLL CONTAINER: it
         renders no scrollbar and ignores the wheel, but the browser can still scroll it
         programmatically — and it did, on every aircraft click, taking the whole form off
         screen with no way for the operator to bring it back. `clip` clips identically and
         creates no scroll container at all, so this box cannot reach a scrolled state
         nobody can undo. The cause of the overflow it was scrolling to is fixed in
         `MissionReadinessRail`; this is the class of trap being closed, not the cause. */
      className={`
        flex flex-col min-h-0 overflow-clip
        lg:w-[var(--dock-sheet-width)] lg:shrink-0 lg:relative lg:z-auto lg:h-auto
        fixed bottom-0 left-0 right-0 z-40 max-w-[100vw]
        rounded-t-xl border-t border-subtle bg-popover/90 backdrop-blur-sm
        shadow-sheet
        lg:rounded-none lg:border-0 lg:bg-transparent lg:shadow-none lg:backdrop-blur-none
      `}
    >
      <button
        type="button"
        aria-label="Resize compose panel"
        onPointerDown={onResizePointerDown}
        className="hidden lg:block absolute inset-y-0 left-0 z-10 w-2 cursor-ew-resize border-0 bg-transparent p-0 hover:bg-status-blue/30 focus:outline-none focus-visible:bg-primary"
      />

      <div className="flex-1 min-h-0 flex flex-col max-h-[min(70vh,44rem)] lg:max-h-full bg-card/60">
        <div className="flex items-center justify-between px-3 py-1.5 border-b divider shrink-0">
          <span className="text-[11px] uppercase tracking-wider text-foreground">New mission</span>
          <button
            type="button"
            onClick={requestClose}
            aria-label="Close compose panel"
            className="p-1.5 -my-0.5 -mx-1 text-muted-foreground hover:text-foreground transition-colors"
          >
            <X className="size-4" strokeWidth={1.5} aria-hidden="true" />
          </button>
        </div>

        {/* `relative` because this scrolls. A scroll container that is not a containing block
            does not contain the absolutely positioned boxes inside it — they escape its clip
            and keep their un-scrolled static position, which is how a `sr-only` radio ended up
            reported ~1230px below this panel's floor. Anything abspos in here (live regions,
            the rail's inputs) now belongs to this scroller. */}
        <div className="relative flex-1 min-h-0 overflow-y-auto px-3 py-3 space-y-4">
          {/* WHAT — the mission type, first because everything below it depends on it: how many
              points the map accepts, what the launch lane can offer, and which parameters are
              asked for. A 2x3 grid of buttons rather than a dropdown: six is few enough to show
              at once, and on a C2 surface a visible set beats a control the operator has to open
              to learn what the options are. */}
          <section className="space-y-2">
            <span className="text-[10px] uppercase tracking-[0.08em] text-muted-foreground">Type</span>
            <div className={MISSION_TYPE_GRID_CLASS} role="group" aria-label="Mission type">
              {MISSION_TYPES.map((type, index) => {
                /* Withheld, not offered-and-refused. The platform cannot fly these yet, and a
                   control that takes the press and then blocks the launch is the thing this
                   whole surface exists to avoid — the operator learns at LAUNCH that the sortie
                   they planned was never flyable. `isFlyableMissionType` is the one authority
                   on which those are; the server refuses the same set. */
                const unavailable = missionTypeUnavailableReason(type);
                const tipOpen = tipType === type;
                return (
                  <div key={type} className="relative">
                    <button
                      type="button"
                      aria-pressed={missionType === type}
                      onClick={() => requestTypeSwitch(type)}
                      disabled={saving || unavailable !== null}
                      className={cn(
                        /* `pr-7` leaves the "?" its own 24px column so the glyph never sits on
                           top of a long label like OUT & BACK at a narrow panel width. */
                        'w-full py-1.5 pl-2 pr-7 text-left text-[10px] uppercase tracking-wider border transition-colors',
                        unavailable !== null
                          /* Greyed out: a filled `muted` block with no crisp border, against
                             available types that are transparent-on-background inside a bright
                             `border` rule. That inversion is what reads as inactive at a glance
                             — the panel's usual dead-control recess (`bg-elevated`) is only two
                             values off `bg-background` and disappears in a grid of its own kind.
                             Text stays at full `muted-foreground` (3.7:1 dark, 5.0:1 light) and
                             is NOT faded further: the operator has to be able to read WHICH two
                             are gone, and hover the "?" to find out why. */
                          ? 'cursor-not-allowed border-divider bg-muted text-muted-foreground'
                          : missionType === type
                            ? 'border-primary bg-primary/15 text-foreground disabled:opacity-50'
                            : 'border-border bg-background text-muted-foreground hover:text-foreground disabled:opacity-50',
                      )}
                    >
                      {MISSION_TYPE_LABELS[type]}
                      {/* What a screen reader cannot see from the grey. Without it the button
                          is announced only as "dimmed", with no reason anywhere on the control. */}
                      {unavailable !== null && <span className="sr-only"> — {unavailable}</span>}
                    </button>

                    {/* The "?" and its tooltip in one region over the cell.

                        On an AVAILABLE type the region is `pointer-events-none` so the type
                        button keeps its own clicks, and only the icon is hittable. On a
                        WITHHELD one it is `-auto`: the button underneath is `disabled`, so
                        there is no click to steal, and hovering the dead tile ANYWHERE shows
                        the reason — which is the gesture an operator actually makes when
                        asking "why is this one off?". Aiming for a 24px glyph is not. */}
                    <div
                      className={cn(
                        'absolute inset-0',
                        unavailable !== null ? 'pointer-events-auto' : 'pointer-events-none',
                      )}
                      onMouseEnter={() => openTip(type, 'hover')}
                      onMouseLeave={() => closeTip(type, 'hover')}
                      /* React's onFocus/onBlur are focusin/focusout, which bubble — so the
                         button inside opens the same tooltip for the keyboard. */
                      onFocus={() => openTip(type, 'focus')}
                      onBlur={() => closeTip(type, 'focus')}
                    >
                      <button
                        type="button"
                        aria-label={`What is ${MISSION_TYPE_LABELS[type]}?`}
                        aria-describedby={tipOpen ? MISSION_TYPE_TIP_ID : undefined}
                        /* Deliberately NOT disabled with its type: reading what an unavailable
                           mission IS is exactly what an operator needs from it.

                           A POINTER click falls through to the tile's own action. The icon
                           covers the rightmost 24px of the button — its `pr-7` gutter, which
                           still looks and feels like button — and a press there used to do
                           nothing at all: no selection, no error, just a tooltip the operator
                           did not ask for. On a tile that commands a sortie, no press may be a
                           silent no-op. A KEYBOARD activation (`detail === 0`) is excluded:
                           this control is named "What is X?", and Enter on it must not quietly
                           do something its name never promised. The keyboard already has the
                           tooltip from focus, and the tile itself from its own Tab stop. */
                        onClick={(e) => {
                          if (e.detail === 0) return;
                          requestTypeSwitch(type);
                        }}
                        className={cn(
                          'pointer-events-auto absolute inset-y-0 right-0 flex w-6 items-center justify-center',
                          'text-[11px] leading-none transition-colors',
                          'focus:outline-none focus-visible:ring-1 focus-visible:ring-primary',
                          /* Never faded: this is a live control and a text glyph, so it owes
                             4.5:1 on whatever it sits on. `muted-foreground` clears that on the
                             plain tiles but reads only 3.7:1 on a withheld tile's `bg-muted` —
                             and that is the one "?" that MUST be findable, because it is the
                             only route to why the tile is dead. */
                          tipOpen
                            ? 'text-primary'
                            : unavailable !== null
                              ? 'text-foreground'
                              : 'text-muted-foreground hover:text-foreground',
                        )}
                      >
                        ?
                      </button>
                      {tipOpen && (
                        <div
                          id={MISSION_TYPE_TIP_ID}
                          role="tooltip"
                          className={cn(
                            /* Out of flow and above everything, so opening one moves nothing.

                               `pointer-events-none` is load-bearing, not tidiness. The box is
                               240px wide and lands on the two grid rows below it (or, from the
                               bottom row, on the "Switch and drop" confirm). Hoverable, it
                               became the hit target for every control it covered: the operator
                               moved onto FOLLOW ROUTE, clicked, and nothing happened — and
                               because hovering it kept it open, it never got out of the way.
                               Inert, it is a label, and everything under it stays live.

                               Anchored to the cell's OUTER edge: the panel resizes down to
                               288px, where a cell is ~130px and a 240px box hung off the left
                               column's right edge would run past the scroller and clip. */
                            'pointer-events-none absolute top-full z-50 w-60 max-w-[calc(100vw-1.5rem)] pt-1',
                            index % MISSION_TYPE_COLUMNS === 0 ? 'left-0' : 'right-0',
                          )}
                        >
                          {/* `border-border`, not `border-subtle`: a floating surface needs an
                              edge that survives the bright theme, and it is what every other
                              popover in this codebase uses. `border-subtle` is alpha 0.08 and
                              left the box outlined by its shadow alone. */}
                          <div className="space-y-1 border border-border bg-popover p-2 shadow-lg">
                            <p className="text-[10px] uppercase tracking-wider text-foreground">
                              {MISSION_TYPE_LABELS[type]}
                            </p>
                            <p className="text-[11px] leading-snug text-muted-foreground">
                              {MISSION_TYPE_DESCRIPTIONS[type]}
                            </p>
                            {unavailable !== null && (
                              <p className="flex items-start gap-1.5 text-[11px] leading-snug text-status-amber-fg">
                                <TriangleAlert className="size-3.5 shrink-0 mt-px" strokeWidth={1.5} aria-hidden="true" />
                                {unavailable}
                              </p>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
            {pendingTypeSwitch ? (
              /* Inline, beside the control that raised it, rather than in the commit strip at
                 the bottom of a scrolling panel — the operator's attention is here, and the
                 question is about this control, not about the commit. */
              <div className="space-y-1.5 border border-status-amber-fg/40 bg-status-amber-fg/10 p-2">
                <p className="flex items-start gap-1.5 text-[11px] text-status-amber-fg" role="alert">
                  <TriangleAlert className="size-3.5 shrink-0 mt-px" strokeWidth={1.5} aria-hidden="true" />
                  {(() => {
                    const keep = waypointCountRangeForType(pendingTypeSwitch).max;
                    const dropped = waypoints.length - keep;
                    return `${aMissionType(pendingTypeSwitch, { capitalised: true })} takes ${keep === 1 ? 'one point' : `${keep} points`}, so ${dropped} of the ${waypoints.length} you placed will be dropped.`;
                  })()}
                </p>
                <div className="flex gap-2">
                  <Button
                    type="button" variant="outline" size="sm"
                    onClick={() => setPendingTypeSwitch(null)}
                    className="border-border text-muted-foreground"
                  >
                    Keep {MISSION_TYPE_LABELS[missionType]}
                  </Button>
                  <Button
                    type="button" size="sm"
                    onClick={() => {
                      onMissionTypeChange(pendingTypeSwitch);
                      setPendingTypeSwitch(null);
                    }}
                    className="bg-button-destructive hover:bg-button-destructive-hover text-destructive-foreground"
                  >
                    Switch and drop
                  </Button>
                </div>
              </div>
            ) : (
              /* Always the SELECTED type, and NAMED as such. The "?" tooltips never write here
                 — they are drawn out of flow above the form precisely so that hovering one
                 cannot move it — but an open tooltip does sit beside this line, and while this
                 was a bare sentence the two read as one explanation contradicting itself: a box
                 headed "PATROL / LOOP" saying it repeats a route, and under it an unattributed
                 "Flies through a predefined sequence of waypoints" belonging to the selection.
                 The label is what tells the operator which type each of the two is about. */
              <p className="text-[11px] text-muted-foreground">
                <span className="uppercase tracking-wider text-foreground">
                  {MISSION_TYPE_LABELS[missionType]}
                </span>
                {' — '}
                {MISSION_TYPE_DESCRIPTIONS[missionType]}
              </p>
            )}

            {/* The orbit's own parameters. Only an orbit has them, and 022 refuses them on
                anything else, so they appear with the type rather than living permanently in
                the form as fields that mean nothing five times out of six.

                UNREACHABLE while ORBIT is withheld from the picker above, and so is everything
                else keyed on `missionType === 'orbit'` in this file: `orbitRadiusM` and
                `orbitBlocker`, the `orbit` field on the commit draft, `reportedOrbitRadiusM`
                and its page-side `composeOrbitRadiusM`, the `shape: 'circle'` arm path, and the
                "Centre" / "CENTRE SET · r … m" copy in the route summary. `UNFLYABLE_MISSION_TYPES`
                in `nowLaunchGate` goes with them — compose can no longer produce a type it
                would block on.

                Kept deliberately, and this is the sign-off CLAUDE.md's no-dead-code rule wants
                rather than a silent retention: it is the parameter entry for a capability the
                rest of the stack already carries (the column, 022's CHECK constraint, the map's
                ring), waiting only on a circular flight path. Deleting it would have to be
                written again unchanged the day that lands, and one condition re-opens all of
                it. What it costs until then is that none of it is exercised, so nothing will
                catch it rotting — re-verify this whole subsystem when ORBIT is re-enabled
                rather than trusting that it still works. */}
            {missionType === 'orbit' && (
              <div className="space-y-2 pt-1">
                <div className="space-y-1.5">
                  <Label htmlFor="compose-orbit-radius" className="text-[11px] uppercase tracking-wider text-muted-foreground">
                    Orbit radius (m)
                  </Label>
                  <Input
                    id="compose-orbit-radius"
                    type="number"
                    inputMode="numeric"
                    min={MIN_ORBIT_RADIUS_M}
                    max={MAX_ORBIT_RADIUS_M}
                    value={orbitRadiusRaw}
                    onChange={(e) => setOrbitRadiusRaw(e.target.value)}
                    disabled={saving}
                    className="bg-background border-border text-foreground text-xs font-mono tabular-nums"
                  />
                  {orbitBlocker && (
                    <p className="text-[11px] text-status-amber-fg">{orbitBlocker}</p>
                  )}
                </div>
                <div className="space-y-1.5">
                  <span className="block text-[11px] uppercase tracking-wider text-muted-foreground">
                    Direction
                  </span>
                  <div className="flex gap-1" role="group" aria-label="Orbit direction">
                    {([['cw', 'Clockwise'], ['ccw', 'Counter-CW']] as const).map(([value, label]) => (
                      <button
                        key={value}
                        type="button"
                        aria-pressed={orbitDirection === value}
                        onClick={() => setOrbitDirection(value)}
                        disabled={saving}
                        className={cn(
                          'flex-1 px-2 py-1.5 text-[10px] uppercase tracking-wider border transition-colors disabled:opacity-50',
                          orbitDirection === value
                            ? 'border-primary bg-primary/15 text-foreground'
                            : 'border-border bg-background text-muted-foreground hover:text-foreground',
                        )}
                      >
                        {label}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </section>

          {/* WHERE — drawn on the live map, SUMMARISED here.
              Summary only, on purpose. The enumerated list is at the very bottom
              of this panel: a 20-point route rendered here pushed the name, the
              site, the pilot, the altitude, the departure and the aircraft rail
              below the fold, so filling the form meant scrolling past the route
              every time. What stays is what the operator must not have to scroll
              to see — how long the route is, whether it will be saved at all, and
              whether it leaves a keep-in fence. */}
          <section className="space-y-2">
            <div className="flex items-center justify-between gap-2">
              <span className="text-[10px] uppercase tracking-[0.08em] text-muted-foreground">
                {isSinglePoint ? (missionType === 'orbit' ? 'Centre' : 'Point') : 'Route'}
              </span>
              {waypoints.length > 0 && (
                <button
                  type="button"
                  onClick={route.clear}
                  disabled={saving}
                  className="flex items-center gap-1 text-[10px] uppercase tracking-wider text-muted-foreground hover:text-status-red-fg transition-colors disabled:opacity-50"
                >
                  <Trash2 className="size-3" strokeWidth={1.5} aria-hidden="true" />
                  Clear
                </button>
              )}
            </div>
            {/* Placement stopped. The route survives and so does this panel, so
                the way back has to be visible — the same affordance the edit
                panel has carried since it was built (`CopMissionEdit`). */}
            {!armed && (
              <div className="space-y-2 rounded border border-subtle bg-background/40 p-2.5">
                <p className="text-[11px] text-muted-foreground">
                  {waypoints.length === 0
                    ? 'Placing is stopped, and nothing has been placed yet.'
                    : 'Placing is stopped. The route is kept.'}
                </p>
                <Button
                  type="button" size="sm" onClick={onArm} disabled={saving}
                  className="h-7 px-2.5 text-[10px] uppercase tracking-wider bg-button-primary hover:bg-button-primary-hover text-primary-foreground"
                >
                  <MapPin className="size-3 mr-1" strokeWidth={1.5} aria-hidden="true" />
                  {waypoints.length === 0 ? 'Place points' : 'Resume placing'}
                </Button>
              </div>
            )}
            {waypoints.length === 0 ? (
              armed ? (
                // No mention of the double-click while the route is empty: there
                // it PLACES the first point rather than finishing (#641). Esc is
                // the way to stop, and it stops placing rather than cancelling
                // anything — nothing here discards a route, only the panel's own
                // Cancel does, and it asks first.
                <p className="text-xs text-muted-foreground">
                  {missionType === 'orbit'
                    ? 'Click the centre of the orbit on the map.'
                    : isSinglePoint
                      ? 'Click the point on the map. Click again to move it.'
                      : 'Click waypoints on the map. Esc stops placing.'}
                </p>
              ) : null
            ) : waypoints.length === 1 ? (
              // No distance at one point: there is no leg to measure, and "0.00 km" reads as a
              // broken number rather than as an absent one. Keyed on the ROUTE's length rather
              // than on `isSinglePoint`, because #373 gave HOVER a max above 1 while leaving
              // its min at 1 — so the commonest hover of all, hold over one intersection, fell
              // into the branch below and printed the very "1 WP · 0.00 km" this forbids.
              // OUT & BACK has had that shape, and that defect, since it got `min: 1`.
              //
              // WHAT the one point is called still depends on the type, and on its MINIMUM:
              // a type complete at one point has its point SET, while a FOLLOW ROUTE with one
              // point is a route still being drawn and must keep reading as a count — the
              // warning underneath is what tells the operator it will not be saved.
              <p className="text-xs font-mono text-foreground tabular-nums">
                {missionType === 'orbit'
                  ? `CENTRE SET · r ${orbitRadiusM ?? '—'} m`
                  : waypointCountRangeForType(missionType).min === 1
                    ? 'POINT SET'
                    : '1 WP'}
              </p>
            ) : (
              <p className="text-xs font-mono text-foreground tabular-nums">
                {waypoints.length} WP · {(distanceM / 1000).toFixed(2)} km
                {/* Which point it stops over. With one point that was never a question; with
                    three it is the whole difference between this type and FOLLOW ROUTE, and
                    the numbered markers alone do not say which one the aircraft holds at. */}
                {missionType === 'hover' && (
                  <span className="text-muted-foreground"> · holds at WP{waypoints.length}</span>
                )}
                {atCap && <span className="ml-2 text-status-amber-fg">at the {MAX_ROUTE_WAYPOINTS}-point limit</span>}
              </p>
            )}
            {/* How to STOP, said in the state where the gesture actually does it.
                It used to live only in the empty-route branch, which is the one
                state where a double-click means something else — so an operator
                with a route drawn was never told how to finish at all. */}
            {armed && waypoints.length > 0 && (
              <p className="text-[11px] text-muted-foreground">
                {isSinglePoint
                  ? 'Click again to move the point. Esc stops placing.'
                  : 'Double-click the last point, or press Esc, to stop placing.'}
              </p>
            )}
            {gates.routeWarning && (
              <p className="flex items-start gap-1.5 text-[11px] text-status-amber-fg">
                <TriangleAlert className="size-3.5 shrink-0 mt-px" strokeWidth={1.5} aria-hidden="true" />
                {gates.routeWarning}
              </p>
            )}
            {/* The keep-in verdict the canvas never gave. The fences are on screen
                here, so a point outside one is drawn red as it is placed — but the
                reason has to be readable too, not only red. */}
            {firstErrorViolation && (
              <p className="flex items-start gap-1.5 text-[11px] text-status-red-fg" role="alert">
                <TriangleAlert className="size-3.5 shrink-0 mt-px" strokeWidth={1.5} aria-hidden="true" />
                {firstErrorViolation.message}
              </p>
            )}
          </section>

          {/* Identity and crew. */}
          <section className="space-y-3">
            <div className="space-y-1.5">
              <Label htmlFor="compose-name" className="text-[11px] uppercase tracking-wider text-muted-foreground">
                Name
              </Label>
              <Input
                id="compose-name"
                value={name}
                maxLength={MISSION_NAME_MAX + 1}
                onChange={(e) => setNameOverride(e.target.value)}
                disabled={saving}
                className="bg-background border-border text-foreground text-xs"
              />
            </div>

            <RailSelect
              id="compose-site"
              label="Site"
              value={siteId}
              options={siteOptions}
              emptyHint={loadError ? unavailable : 'No sites yet'}
              disabled={saving || !ready}
              onChange={setSiteId}
            />
            <RailSelect
              id="compose-pilot"
              label="Pilot"
              value={pilotId}
              options={pilotOptions}
              emptyHint={loadError ? unavailable : 'No pilots yet'}
              disabled={saving || !ready}
              onChange={setPilotId}
            />

            <div className="space-y-1.5">
              <Label htmlFor="compose-altitude" className="text-[11px] uppercase tracking-wider text-muted-foreground">
                Altitude (m AGL)
              </Label>
              <Input
                id="compose-altitude"
                type="number"
                inputMode="numeric"
                min={CANVAS_ALT_MIN_M}
                max={CANVAS_ALT_MAX_M}
                value={altitudeRaw}
                onChange={(e) => setAltitudeRaw(e.target.value)}
                disabled={saving}
                aria-describedby="compose-altitude-hint"
                className="bg-background border-border text-foreground text-xs font-mono tabular-nums"
              />
              {/* The mission altitude is the DEFAULT for the whole route: every
                  waypoint is attached to it, and changing it moves every point the
                  operator has not given an altitude of its own. Points they HAVE
                  set stay where they were put — see `RouteWaypointList`. */}
              <p id="compose-altitude-hint" className="text-[10px] text-muted-foreground">
                Attached to every waypoint. Set a waypoint&apos;s own below to override it.
              </p>
              {/* Reported, never applied silently: quietly rewriting an altitude the
                  operator typed is the wrong default on a military product. */}
              {altitude.note && (
                <p className="text-[11px] text-status-amber-fg">{altitude.note}</p>
              )}
            </div>
          </section>

          {/* WHEN. `later` is the default and always will be: NOW commands a real takeoff. */}
          <section className="space-y-2">
            <span className="text-[10px] uppercase tracking-[0.08em] text-muted-foreground">When</span>
            <div className="flex gap-1" role="group" aria-label="When to fly">
              {(['later', 'now'] as const).map((mode) => (
                <button
                  key={mode}
                  type="button"
                  aria-pressed={whenMode === mode}
                  disabled={saving}
                  onClick={() => {
                    setWhenMode(mode);
                    // Arming does not survive a lane change.
                    commit.disarmLaunch();
                  }}
                  className={cn(
                    'flex-1 rounded border px-2 py-1.5 text-[10px] uppercase tracking-wider transition-colors',
                    whenMode === mode
                      ? mode === 'now'
                        ? 'border-status-red/50 bg-status-red/15 text-status-red-fg'
                        : 'border-primary/50 bg-primary/10 text-primary'
                      : 'border-border text-muted-foreground hover:text-foreground',
                  )}
                >
                  {/* Not "Launch now" — that is the commit button below, and two
                      controls with the same name is a trap for a screen reader
                      and for a hurried operator alike. This one only chooses
                      the lane. */}
                  {mode === 'later' ? 'Plan it' : 'Now'}
                </button>
              ))}
            </div>
            {whenMode === 'later' && (
              <div className="space-y-1.5">
                <Label htmlFor="compose-departure" className="text-[11px] uppercase tracking-wider text-muted-foreground">
                  Departure (UTC) <span className="lowercase tracking-normal opacity-70">optional</span>
                </Label>
                <Input
                  id="compose-departure"
                  type="datetime-local"
                  value={scheduledAt}
                  onChange={(e) => setScheduledAt(e.target.value)}
                  disabled={saving}
                  className="bg-background border-border text-foreground text-xs font-mono tabular-nums"
                />
                {/* What the operator's own clock will read when this fires.
                    The box is UTC and the clock in the top bar is the org's
                    zone, so without this the two references an operator does
                    schedule arithmetic against are three hours apart with only
                    a label between them - `MissionSidebar` reached the same
                    conclusion for the identical field on the missions page.
                    Rendered in the ORG's zone rather than the browser's, so it
                    matches that clock rather than introducing a third reading
                    for an operator working remotely. */}
                {localDeparture && (
                  <p className="text-[10px] font-mono tabular-nums text-muted-foreground">
                    Local: {localDeparture}
                  </p>
                )}
              </div>
            )}
          </section>

          {/*
            HOLD THIS STATION - an INTENT, not a configuration.

            Shown only for a type the server says can carry coverage (`POST /api/coverage` refuses
            every other one with a reason, and `capable_types` is the allow-list) - and only when
            that list has actually been read. `null` is "not known yet" rather than "nothing
            qualifies", so a slow fetch hides the offer instead of a failed one silently removing
            a capability.

            Ticking it configures NOTHING here. On a successful create the COP opens the real
            attach wizard, whole, pre-filled from the mission just made. Flattening that wizard
            into a fourth compose step would cost its three-tabs-over-one-form shape, its per-step
            refusal counts and its live projection - and its own header records that a linear
            wizard "would make the common case slower than the form it replaces, which is how a
            redesign loses to the thing it fixed".

            IT DOES NOT BLOCK THE CREATE. A mission that exists without its coverage is a
            recoverable state the product already understands; a create that rolls back because a
            second form was cancelled is not.
          */}
          {/* Optional chaining, not `!== null`: an allow-list that is absent entirely is the same
              answer as one that is unknown - do not offer it - and this way a caller that has not
              been given the prop yet renders nothing rather than throwing on `undefined`. */}
          {offerable && (
            <section className="space-y-1.5">
              <div className="flex items-start gap-2">
                {/* The house checkbox, not a bespoke one: `size-4 rounded border-input bg-background
                    accent-primary` is what every other checkbox in this app uses. The first cut was
                    14 px (under SC 2.5.8), carried no `border-input bg-background` so it rendered as
                    a white UA default box in a dark COP, and used a STATUS green for a selection -
                    in this palette green means nominal, not chosen. */}
                <input
                  id="compose-hold-station"
                  type="checkbox"
                  checked={holdContinuously}
                  onChange={(e) => setHoldContinuously(e.target.checked)}
                  disabled={saving}
                  className="mt-0.5 size-4 rounded border-input bg-background accent-primary cursor-pointer"
                />
                <div className="min-w-0">
                  <Label
                    htmlFor="compose-hold-station"
                    className="text-[11px] uppercase tracking-wider text-muted-foreground cursor-pointer"
                  >
                    Hold this station continuously
                  </Label>
                  <p className="text-[10px] leading-snug text-muted-foreground mt-0.5">
                    {/* Once ticked, the rail directly below says what happens to the AIRCRAFT, so
                        this says only what this box is for. The two used to end on the same clause
                        - one saying the aircraft are chosen later, one saying the aircraft above is
                        the first - which is the "did I pick one or two?" question this is meant to
                        answer. */}
                    {holdContinuously
                      ? 'The window and the battery thresholds are set after the mission is created.'
                      : 'Keep the last waypoint manned by rotating aircraft through it.'}
                  </p>
                </div>
              </div>
            </section>
          )}

          {/* WHO — ranked by who can actually fly the route as drawn. */}
          <MissionReadinessRail
            drones={drones}
            loadError={loadError}
            waypoints={waypoints}
            value={droneId}
            onChange={setDroneId}
            name="compose-aircraft"
            disabled={saving}
            copy={holdRailCopy}
            onSelectedVerdict={setSelectedVerdict}
          />

          {/* The route, point by point — LAST in the panel, and last for a reason.
              It is the only section whose height is set by the operator's own
              work: a route can be a hundred points long, and anything below it
              moves every time one is placed. Everything an operator has to fill
              in therefore sits above it, at a position that does not depend on
              how long the route got. */}
          {waypoints.length > 0 && (
            <section className="space-y-2">
              <div className="flex items-baseline justify-between gap-2">
                <span className="text-[10px] uppercase tracking-[0.08em] text-muted-foreground">
                  Waypoints
                </span>
                <span className="text-[10px] text-muted-foreground">
                  Blank altitude follows the mission
                </span>
              </div>
              <RouteWaypointList
                waypoints={waypoints}
                selectedIndex={selectedIndex}
                invalidIndices={invalidIndices}
                missionAltitudeM={altitude.meters}
                onSelect={route.select}
                onAltitudeChange={route.setAltitudeAt}
                onRemove={route.removeAt}
                disabled={saving}
                className="max-h-64"
              />
            </section>
          )}
        </div>

        {/* The commit strip: what is blocking, then the action it gates. */}
        <div className="shrink-0 border-t divider px-3 py-2 space-y-2 bg-card/80">
          {whenMode === 'now' ? (
            <>
              {nowGate.blockers.map((blocker) => (
                <span key={blocker} className="flex items-start gap-1.5 text-[11px] text-status-amber-fg">
                  <TriangleAlert className="size-3.5 shrink-0 mt-px" strokeWidth={1.5} aria-hidden="true" />
                  {blocker}
                </span>
              ))}
              {nowGate.blockers.length === 0 && (
                <span
                  className={cn(
                    'flex items-start gap-1.5 text-[11px]',
                    nowGate.advisory ? 'text-status-amber-fg' : 'text-status-green-fg',
                  )}
                >
                  <CheckCircle2 className="size-3.5 shrink-0 mt-px" strokeWidth={1.5} aria-hidden="true" />
                  {nowGate.advisory ?? 'Aircraft and route ready'}
                </span>
              )}
              {/* WHAT AN ORBIT WILL DO, BEFORE THE PRESS AND NOT ONLY AFTER IT (#700 review HIGH).
                  A circle never ends, and until this line the only place that was ever said was
                  inside a refusal - so on the path that IS allowed the operator read "Aircraft
                  and route ready" and nothing else. Amber and alongside the green line rather
                  than replacing it: nothing is blocking, and this is not a warning that the
                  launch will fail. It is what the launch will DO. */}
              {missionType === 'orbit' && nowGate.blockers.length === 0 && (
                <span className="flex items-start gap-1.5 text-[11px] text-status-amber-fg">
                  <TriangleAlert className="size-3.5 shrink-0 mt-px" strokeWidth={1.5} aria-hidden="true" />
                  {orbitLaunchWarning(orbitRadiusM !== null ? { radiusM: orbitRadiusM, direction: orbitDirection } : null)}
                </span>
              )}
              {/* Docks off, the aircraft still linked to a dock (slice 3): a QUESTION, on the strip
                  itself and in amber, with its two answers - not a red error with no control. */}
              {commit.dockLinkQuestion !== null && (
                <GotoReliefQuestionBar
                    claimEscape={false}
                  pending={{ code: 'dock_link_present_launch', message: commit.dockLinkQuestion.message }}
                  onConfirm={commit.confirmDockLink}
                  onCancel={commit.dismissDockLink}
                />
              )}
              {launchError && (
                <span className="flex items-start gap-1.5 text-[11px] text-status-red-fg" role="alert">
                  <TriangleAlert className="size-3.5 shrink-0 mt-px" strokeWidth={1.5} aria-hidden="true" />
                  {launchError}
                </span>
              )}
            </>
          ) : (
            <>
              {gates.readiness.items.map((item) => {
                const Icon = item.satisfied ? CheckCircle2 : Circle;
                return (
                  <span
                    key={item.key}
                    className={cn(
                      'flex items-start gap-1.5 text-[11px]',
                      item.satisfied ? 'text-status-green-fg' : 'text-muted-foreground',
                    )}
                  >
                    <Icon className="size-3.5 shrink-0 mt-px" strokeWidth={1.5} aria-hidden="true" />
                    {item.satisfied ? item.label : item.blocker}
                  </span>
                );
              })}
              <span className="block text-[11px] text-muted-foreground">
                Creates as a draft — only the route is needed to schedule it.
              </span>
            </>
          )}

          {/* Arming a real takeoff must be announced, not only styled. */}
          <p role="status" aria-live="assertive" className="sr-only">
            {commit.launchArmed
              ? 'Armed. Press Confirm launch to command a takeoff now. Hold to cancel.'
              : confirmDiscard
                ? (routeRescuePending
                    ? 'Leave without the route? The mission is saved; the route you drew is not, and will be lost.'
                    : 'Discard this mission? Keep planning to go back.')
                : ''}
          </p>

          <div className="flex flex-wrap items-center justify-end gap-2">
            {confirmDiscard ? (
              <>
                <span className="text-[11px] text-status-amber-fg">
                  {routeRescuePending ? 'Leave? The route is not saved and will be lost.' : 'Discard this mission?'}
                </span>
                <Button
                  type="button" variant="outline" size="sm"
                  onClick={() => setConfirmDiscard(false)}
                  className="border-border text-muted-foreground"
                >
                  {routeRescuePending ? 'Keep the route' : 'Keep planning'}
                </Button>
                <Button
                  type="button" size="sm"
                  onClick={() => (routeRescuePending ? onCreated(commit.routeRescueMissionId!) : onClose())}
                  className="bg-button-destructive hover:bg-button-destructive-hover text-destructive-foreground"
                >
                  {routeRescuePending ? 'Leave — lose the route' : 'Discard'}
                </Button>
              </>
            ) : routeRescuePending ? (
              <>
                <span className="flex items-start gap-1.5 text-[11px] text-status-amber-fg">
                  <TriangleAlert className="size-3.5 shrink-0 mt-px" strokeWidth={1.5} aria-hidden="true" />
                  Mission created — its route is not saved
                </span>
                <Button
                  type="button" variant="outline" size="sm" disabled={saving}
                  onClick={() => setConfirmDiscard(true)}
                  className="border-border text-muted-foreground hover:bg-accent"
                >
                  Discard route
                </Button>
                <Button
                  type="button" size="sm" disabled={saving}
                  onClick={() => void commit.retryRoute()}
                  className="bg-button-primary hover:bg-button-primary-hover text-primary-foreground"
                >
                  <RotateCw className="size-4 mr-1.5" strokeWidth={1.5} aria-hidden="true" />
                  {saving ? 'Retrying…' : 'Retry route'}
                </Button>
              </>
            ) : whenMode === 'now' ? (
              <>
                {launchRescuePending && (
                  <span className="flex items-start gap-1.5 text-[11px] text-status-amber-fg">
                    <TriangleAlert className="size-3.5 shrink-0 mt-px" strokeWidth={1.5} aria-hidden="true" />
                    Mission and route saved — not launched
                  </span>
                )}
                <Button
                  type="button" variant="outline" size="sm" disabled={saving}
                  onClick={() => {
                    if (commit.launchArmed) { commit.disarmLaunch(); return; }
                    if (launchRescuePending) { onCreated(commit.launchRescueMissionId!); return; }
                    requestClose();
                  }}
                  className="border-border text-muted-foreground hover:bg-accent"
                >
                  {commit.launchArmed ? 'Hold' : launchRescuePending ? 'Leave it planned' : 'Cancel'}
                </Button>
                {/* Two presses, always. The first arms; the second commands. Not a
                    modal on purpose: the route, the aircraft, the blockers and the
                    live picture all stay on screen — which is what the operator has
                    to be looking at when they confirm a takeoff. */}
                <Button
                  type="button" size="sm"
                  onClick={() => void commit.launchNow()}
                  disabled={saving || !nowGate.canLaunch}
                  className={cn(
                    'uppercase tracking-wider',
                    commit.launchArmed
                      ? 'bg-button-destructive hover:bg-button-destructive-hover text-destructive-foreground'
                      : 'border border-status-red/50 bg-status-red/15 text-status-red-fg hover:bg-status-red/25',
                  )}
                >
                  <Rocket className="size-4 mr-1.5" strokeWidth={1.5} aria-hidden="true" />
                  {saving
                    ? 'Launching…'
                    : commit.launchArmed
                      ? 'Confirm launch'
                      : launchRescuePending ? 'Retry launch' : 'Launch now'}
                </Button>
              </>
            ) : (
              <>
                <Button
                  type="button" variant="outline" size="sm" onClick={requestClose} disabled={saving}
                  className="border-border text-muted-foreground hover:bg-accent"
                >
                  Cancel
                </Button>
                <Button
                  type="button" size="sm" onClick={() => void commit.create()}
                  disabled={saving || !gates.canCreate}
                  className="bg-button-primary hover:bg-button-primary-hover text-primary-foreground"
                >
                  <Check className="size-4 mr-1.5" strokeWidth={1.5} aria-hidden="true" />
                  {saving ? 'Creating…' : 'Create mission'}
                </Button>
              </>
            )}
          </div>
          {keepInFences.length === 0 && waypoints.length > 0 && (
            <p className="text-[10px] text-muted-foreground">
              No active keep-in zones — route containment was not checked.
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
